/**
 * Debounced auto-save hook with field grouping
 * Watches for dirty fields, groups them by save handler, and batches saves
 */

import * as React from "react";
import type { ApplicationFormData } from "../types/applicationTypes";
import { isAutosaveFieldFailure } from "./autosaveFieldFailure";

// ============================================================================
// Types
// ============================================================================

/**
 * Save handler function type.
 *
 * Acknowledgement is status-based: a handler that resolves is taken to have
 * persisted all the fields it was given. A handler that throws marks all of its
 * fields failed unless it throws `AutosaveFieldFailureError`, which scopes
 * failure to specific field paths while other fields in the batch are treated
 * as saved. (Handlers that cannot save some fields should throw or be gated
 * upstream so unsaved fields stay dirty — see the co-borrower-id gate.)
 */
export type SaveHandler<T = unknown> = (
  data: T,
  dirtyFields: Set<string>,
) => Promise<void>;

/**
 * Field-to-handler mapping configuration
 * Maps field paths to their respective save handlers
 */
export interface SaveHandlerConfig {
  /** Handler for borrower data changes */
  borrower?: SaveHandler<ApplicationFormData["primaryBorrower"]>;
  /** Handler for co-borrower data changes */
  coBorrower?: SaveHandler<ApplicationFormData["coBorrower"]>;
  /** Handler for home data changes */
  home?: SaveHandler<ApplicationFormData["home"]>;
  /** Handler for land data changes */
  land?: SaveHandler<ApplicationFormData["land"]>;
  /** Handler for pricing data changes */
  pricing?: SaveHandler<ApplicationFormData["pricing"]>;
  /** Handler for lenders data changes */
  lenders?: SaveHandler<ApplicationFormData["lenders"]>;
  /** Handler for signature data changes */
  signature?: SaveHandler<ApplicationFormData["signature"]>;
  /** Fallback handler for any field not matched above */
  default?: SaveHandler<ApplicationFormData>;
}

/**
 * Auto-save options
 */
export interface AutoSaveOptions {
  /** Debounce delay in milliseconds (default: 1000) */
  debounceMs?: number;
  /** Maximum wait time before forcing a save (default: 5000) */
  maxWaitMs?: number;
  /** Whether auto-save is enabled (default: true) */
  enabled?: boolean;
  /** Callback when save starts */
  onSaveStart?: () => void;
  /** Callback when save completes successfully */
  onSaveSuccess?: () => void;
  /** Callback when save fails */
  onSaveError?: (error: Error, failedFields: Set<string>) => void;
  /** Field paths quarantined from background autosave until re-edited */
  pausedAutoSaveFields?: Set<string>;
  /** Called when fields should be paused after a field-scoped save failure */
  onPauseAutoSaveFields?: (fields: Set<string>) => void;
  /**
   * Shared with save handlers via getSuppressHandlerToasts. flushSave toggles
   * this during validation/poll flushes so handlers skip toasts (field errors
   * still apply). Required — host must wire the same ref into createAutoSaveHandlers.
   */
  suppressHandlerToastsRef: React.MutableRefObject<boolean>;
}

/**
 * Auto-save state
 */
export interface AutoSaveState {
  /** Whether a save is currently in progress */
  isSaving: boolean;
  /** The last error that occurred during save */
  lastError: Error | null;
  /** Timestamp of the last successful save */
  lastSavedAt: Date | null;
  /** Number of pending changes waiting to be saved */
  pendingChanges: number;
}

/**
 * Return type for the hook
 */
export interface FlushSaveOptions {
  /**
   * Skip handler-level toasts (e.g. during manual validation flush).
   * Currently only the borrower duplicate-contact path in createAutoSaveHandlers
   * reads suppressHandlerToastsRef; any new handler toasts must honor it too.
   */
  suppressHandlerToasts?: boolean;
  /**
   * Retry quarantined (paused) fields during this flush. Default true.
   * Set false for automatic signature progress polls to avoid re-hitting failed
   * contact fields every poll interval.
   */
  includePausedFields?: boolean;
}

export interface UseDebouncedAutoSaveReturn extends AutoSaveState {
  /** Force an immediate save */
  forceSave: () => Promise<void>;
  /** Flush debounced pending fields immediately and wait for idle */
  flushSave: (fields: Set<string>, options?: FlushSaveOptions) => Promise<void>;
  /** Cancel pending save */
  cancelPendingSave: () => void;
}

// ============================================================================
// Field Path to Handler Key Mapping
// ============================================================================

type HandlerKey = keyof SaveHandlerConfig;

const getHandlerKeyForFieldPath = (fieldPath: string): HandlerKey => {
  if (fieldPath.startsWith("primaryBorrower")) return "borrower";
  if (fieldPath.startsWith("coBorrower")) return "coBorrower";
  if (fieldPath.startsWith("home")) return "home";
  if (fieldPath.startsWith("land")) return "land";
  if (fieldPath.startsWith("pricing")) return "pricing";
  if (fieldPath.startsWith("lenders")) return "lenders";
  if (fieldPath.startsWith("signature")) return "signature";
  return "default";
};

const EMPTY_PAUSED_AUTO_SAVE_FIELDS = new Set<string>();

const hadPausedFieldRemoved = (
  previous: Set<string>,
  current: Set<string>,
): boolean => {
  for (const field of previous) {
    if (!current.has(field)) {
      return true;
    }
  }
  return false;
};

// ============================================================================
// Hook Implementation
// ============================================================================

export function useDebouncedAutoSave(
  /** Current application data */
  data: ApplicationFormData,
  /** Set of dirty field paths */
  dirtyFields: Set<string>,
  /** Callback to clear ONLY the confirmed-saved field paths from dirty state */
  clearSavedFields: (fields: Set<string>) => void,
  /** Save handler configuration */
  handlers: SaveHandlerConfig,
  /** Options */
  options: AutoSaveOptions,
): UseDebouncedAutoSaveReturn {
  const {
    debounceMs = 1000,
    maxWaitMs = 5000,
    enabled = true,
    onSaveStart,
    onSaveSuccess,
    onSaveError,
    pausedAutoSaveFields = EMPTY_PAUSED_AUTO_SAVE_FIELDS,
    onPauseAutoSaveFields,
    suppressHandlerToastsRef,
  } = options;

  const onSaveStartRef = React.useRef(onSaveStart);
  const onSaveSuccessRef = React.useRef(onSaveSuccess);
  const onSaveErrorRef = React.useRef(onSaveError);
  const onPauseAutoSaveFieldsRef = React.useRef(onPauseAutoSaveFields);
  const pausedAutoSaveFieldsRef = React.useRef(pausedAutoSaveFields);
  const flushIncludePausedRef = React.useRef(false);
  const handlersRef = React.useRef(handlers);
  const prevPausedFieldsRef = React.useRef(pausedAutoSaveFields);
  handlersRef.current = handlers;
  onSaveStartRef.current = onSaveStart;
  onSaveSuccessRef.current = onSaveSuccess;
  onSaveErrorRef.current = onSaveError;
  onPauseAutoSaveFieldsRef.current = onPauseAutoSaveFields;
  pausedAutoSaveFieldsRef.current = pausedAutoSaveFields;

  // State
  const [state, setState] = React.useState<AutoSaveState>({
    isSaving: false,
    lastError: null,
    lastSavedAt: null,
    pendingChanges: 0,
  });

  // Refs for timers and tracking
  const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const maxWaitTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const pendingFieldsRef = React.useRef<Set<string>>(new Set());
  const lastDataRef = React.useRef<ApplicationFormData>(data);
  // Track dirty fields snapshot to detect actual changes
  const lastDirtyFieldsSnapshotRef = React.useRef<string>("");
  // Guard against concurrent executeSave calls
  const isSavingRef = React.useRef(false);
  // Fields deferred until prerequisites exist (e.g. applicationId, coBorrowerId)
  const deferredFieldsRef = React.useRef<Set<string>>(new Set());

  // Clear timers helper
  const clearTimers = React.useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (maxWaitTimerRef.current) {
      clearTimeout(maxWaitTimerRef.current);
      maxWaitTimerRef.current = null;
    }
  }, []);

  // Track fields from the most recent save to prevent re-adding them
  // to pendingFieldsRef on the next effect run (breaks re-dirty cycle).
  const lastSavedFieldsRef = React.useRef<Set<string> | null>(null);
  // Field paths re-edited while a save is in flight. The in-flight request
  // carried their OLD value, so they must not be treated as persisted when it
  // resolves — they stay dirty and a follow-up save sends the new value.
  const reDirtiedDuringSaveRef = React.useRef<Set<string>>(new Set());

  // Execute save for grouped fields
  const executeSave = React.useCallback(async () => {
    // Prevent concurrent saves — pending fields stay for the next cycle
    if (isSavingRef.current) {
      return;
    }
    if (pendingFieldsRef.current.size === 0) {
      return;
    }

    const includePaused = flushIncludePausedRef.current;
    const fieldsToSave = new Set(pendingFieldsRef.current);
    if (!includePaused) {
      for (const f of fieldsToSave) {
        if (pausedAutoSaveFieldsRef.current.has(f)) {
          fieldsToSave.delete(f);
        }
      }
    }
    if (fieldsToSave.size === 0) {
      clearTimers();
      return;
    }
    isSavingRef.current = true;
    clearTimers();

    // Remove the fields we're about to attempt from the pending ref. Fields
    // that become dirty DURING this save are re-added by the watch effect and
    // are intentionally NOT part of this attempt — they save on the next cycle.
    fieldsToSave.forEach((f) => pendingFieldsRef.current.delete(f));

    // Start tracking edits made while this save is in flight.
    reDirtiedDuringSaveRef.current = new Set();

    setState((prev) => ({ ...prev, isSaving: true, pendingChanges: 0 }));
    onSaveStartRef.current?.();

    // Fields the backend confirmed, and fields whose handler failed.
    const savedFields = new Set<string>();
    const failedFields = new Set<string>();
    const fieldsPausedThisCycle = new Set<string>();
    const skippedNoHandlerFields = new Set<string>();
    let saveError: Error | null = null;

    try {
      // Group fields by handler
      const handlerGroups = new Map<HandlerKey, Set<string>>();

      fieldsToSave.forEach((fieldPath) => {
        const handlerKey = getHandlerKeyForFieldPath(fieldPath);
        if (!handlerGroups.has(handlerKey)) {
          handlerGroups.set(handlerKey, new Set());
        }
        handlerGroups.get(handlerKey)?.add(fieldPath);
      });

      // Execute handlers in parallel
      const savePromises: Promise<void>[] = [];
      const saveFieldSets: Set<string>[] = [];
      const hasAppId = !!lastDataRef.current.applicationId;

      const hasCoBorrowerId = !!lastDataRef.current.coBorrowerId;

      handlerGroups.forEach((fields, handlerKey) => {
        // Without an applicationId, only the borrower handler runs (it creates
        // the draft). Other fields are deferred until applicationId is set:
        // they stay dirty (never cleared) and flush once the id arrives.
        if (!hasAppId && handlerKey !== "borrower") {
          fields.forEach((f) => deferredFieldsRef.current.add(f));
          return;
        }

        // Co-borrower fields can't be saved until the co-borrower record
        // exists. Defer them (keep dirty) rather than letting the handler
        // no-op and falsely report them as saved — they flush once the
        // coBorrowerId arrives (watched by the deferred-flush effect below).
        if (handlerKey === "coBorrower" && !hasCoBorrowerId) {
          fields.forEach((f) => deferredFieldsRef.current.add(f));
          return;
        }

        const handler = handlersRef.current[handlerKey];
        if (!handler) {
          fields.forEach((f) => {
            pendingFieldsRef.current.add(f);
            skippedNoHandlerFields.add(f);
          });
          return;
        }

        // Get the data slice for this handler
        let dataSlice: unknown;
        switch (handlerKey) {
          case "borrower":
            dataSlice = lastDataRef.current.primaryBorrower;
            break;
          case "coBorrower":
            dataSlice = lastDataRef.current.coBorrower;
            break;
          case "home":
            dataSlice = lastDataRef.current.home;
            break;
          case "land":
            dataSlice = lastDataRef.current.land;
            break;
          case "pricing":
            dataSlice = lastDataRef.current.pricing;
            break;
          case "lenders":
            dataSlice = lastDataRef.current.lenders;
            break;
          case "signature":
            dataSlice = lastDataRef.current.signature;
            break;
          case "default":
            dataSlice = lastDataRef.current;
            break;
        }

        if (dataSlice === undefined) {
          fields.forEach((f) => {
            pendingFieldsRef.current.add(f);
            skippedNoHandlerFields.add(f);
          });
          return;
        }

        // Type assertion needed due to dynamic handler selection
        saveFieldSets.push(fields);
        savePromises.push(
          (handler as SaveHandler<typeof dataSlice>)(dataSlice, fields),
        );
      });

      const results = await Promise.allSettled(savePromises);

      results.forEach((r, i) => {
        const requested = saveFieldSets[i];
        if (r.status === "fulfilled") {
          requested.forEach((f) => savedFields.add(f));
        } else if (isAutosaveFieldFailure(r.reason)) {
          const fieldFailure = r.reason;
          fieldFailure.failedFields.forEach((f) => {
            failedFields.add(f);
            fieldsPausedThisCycle.add(f);
          });
          requested.forEach((f) => {
            if (!fieldFailure.failedFields.has(f)) {
              savedFields.add(f);
            }
          });
          if (!saveError) {
            saveError = fieldFailure;
          }
        } else {
          requested.forEach((f) => failedFields.add(f));
          if (!saveError) {
            saveError =
              r.reason instanceof Error ? r.reason : new Error("Save failed");
          }
        }
      });

      if (fieldsPausedThisCycle.size > 0) {
        onPauseAutoSaveFieldsRef.current?.(fieldsPausedThisCycle);
        fieldsPausedThisCycle.forEach((f) =>
          pendingFieldsRef.current.delete(f),
        );
      }
    } catch (error) {
      // Defensive: an error thrown outside per-handler settlement.
      saveError = error instanceof Error ? error : new Error("Save failed");
      fieldsToSave.forEach((f) => failedFields.add(f));
    } finally {
      isSavingRef.current = false;
      const finalError: Error | null = saveError;
      const didError = failedFields.size > 0 || finalError !== null;

      // A field re-edited while this save was in flight must NOT be treated as
      // persisted — the request carried its old value. Exclude those from the
      // confirmed-saved set so they stay dirty and get sent by the follow-up.
      const reDirtied = reDirtiedDuringSaveRef.current;
      const confirmedSaved =
        reDirtied.size === 0
          ? savedFields
          : new Set([...savedFields].filter((f) => !reDirtied.has(f)));

      // Clear ONLY the confirmed-saved fields — from both the store dirty set
      // and the pending ref. Fields edited during the in-flight save are NOT
      // confirmed, so they are preserved and saved on the next cycle. This is
      // the core guarantee: a field is forgotten only once persisted.
      if (confirmedSaved.size > 0) {
        clearSavedFields(confirmedSaved);
        confirmedSaved.forEach((f) => pendingFieldsRef.current.delete(f));
        // Suppress the phantom re-dirty an input re-render can trigger for the
        // fields we just saved — but never for unsaved or re-edited fields.
        lastSavedFieldsRef.current = confirmedSaved;
      }

      // Re-queue non-paused failed fields, and saved fields re-edited mid-flight.
      failedFields.forEach((f) => {
        if (
          !fieldsPausedThisCycle.has(f) &&
          !pausedAutoSaveFieldsRef.current.has(f)
        ) {
          pendingFieldsRef.current.add(f);
        }
      });
      savedFields.forEach((f) => {
        if (reDirtied.has(f)) pendingFieldsRef.current.add(f);
      });

      const quarantinedFailuresOnly =
        failedFields.size > 0 &&
        isAutosaveFieldFailure(finalError) &&
        [...failedFields].every((f) => fieldsPausedThisCycle.has(f));
      const partialSuccessWithQuarantine =
        confirmedSaved.size > 0 && quarantinedFailuresOnly;

      if (didError && finalError && !partialSuccessWithQuarantine) {
        setState((prev) => ({
          ...prev,
          isSaving: false,
          lastError: finalError,
        }));
        // Reset the snapshot so the next user edit reschedules a save even if
        // the dirty set is otherwise unchanged.
        lastDirtyFieldsSnapshotRef.current = "";
        onSaveErrorRef.current?.(finalError, failedFields);
      } else {
        setState((prev) => ({
          ...prev,
          isSaving: false,
          lastError: null,
          lastSavedAt: new Date(),
        }));
        if (didError && finalError) {
          lastDirtyFieldsSnapshotRef.current = "";
          onSaveErrorRef.current?.(finalError, failedFields);
        } else {
          onSaveSuccessRef.current?.();
        }
      }

      // On success, flush any fields that became pending during the save (e.g.
      // re-edited mid-flight). On failure, fields stay pending and retry only on
      // the user's next edit or an explicit flushSave call.
      const shouldFollowUpAfterReDirtied =
        didError && reDirtied.size > 0 && !partialSuccessWithQuarantine;
      const onlySkippedNoHandlerPending =
        pendingFieldsRef.current.size > 0 &&
        confirmedSaved.size === 0 &&
        [...pendingFieldsRef.current].every((f) =>
          skippedNoHandlerFields.has(f),
        );
      const shouldRetry =
        pendingFieldsRef.current.size > 0 &&
        !debounceTimerRef.current &&
        !onlySkippedNoHandlerPending &&
        (!didError ||
          partialSuccessWithQuarantine ||
          shouldFollowUpAfterReDirtied);
      if (shouldRetry) {
        // Re-edited mid-flight follow-up must not also retry fields that failed
        // in the same batch (they stay dirty until the user edits again).
        if (shouldFollowUpAfterReDirtied) {
          for (const f of failedFields) {
            pendingFieldsRef.current.delete(f);
          }
        }
        if (pendingFieldsRef.current.size > 0) {
          debounceTimerRef.current = setTimeout(() => {
            debounceTimerRef.current = null;
            void executeSave();
          }, 0);
        }
      }
    }
  }, [clearTimers, clearSavedFields]);

  // Force save
  const forceSave = React.useCallback(async () => {
    await executeSave();
  }, [executeSave]);

  const waitForSaveIdle = React.useCallback(async () => {
    const deadline = Date.now() + 15000;
    while (isSavingRef.current && Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }, []);

  // Flush debounced timers, merge dirty fields into pending, and save immediately.
  const flushSave = React.useCallback(
    async (fields: Set<string>, flushOptions?: FlushSaveOptions) => {
      clearTimers();
      fields.forEach((f) => pendingFieldsRef.current.add(f));

      await waitForSaveIdle();
      if (pendingFieldsRef.current.size > 0) {
        flushIncludePausedRef.current =
          flushOptions?.includePausedFields ?? true;
        suppressHandlerToastsRef.current =
          flushOptions?.suppressHandlerToasts ?? false;
        try {
          await executeSave();
        } finally {
          flushIncludePausedRef.current = false;
          suppressHandlerToastsRef.current = false;
        }
      }
      await waitForSaveIdle();
    },
    [clearTimers, executeSave, waitForSaveIdle, suppressHandlerToastsRef],
  );

  // Cancel pending save
  const cancelPendingSave = React.useCallback(() => {
    clearTimers();
    pendingFieldsRef.current.clear();
    setState((prev) => ({ ...prev, pendingChanges: 0 }));
  }, [clearTimers]);

  // Keep data ref in sync on every render (outside effect to avoid
  // triggering the dirty-field effect on every store update).
  lastDataRef.current = data;

  // Watch for dirty field changes
  React.useEffect(() => {
    if (!enabled) return;

    // Check if dirty fields actually changed (not just a reference change)
    const currentSnapshot = Array.from(dirtyFields).sort().join(",");
    const hadNewFields = currentSnapshot !== lastDirtyFieldsSnapshotRef.current;
    lastDirtyFieldsSnapshotRef.current = currentSnapshot;

    // Add new dirty fields to pending. Skip fields that were just saved
    // to break the re-dirty cycle: after clearDirtyFields(), the input
    // component can re-render and re-mark the field dirty before the user
    // has actually typed again.
    if (lastSavedFieldsRef.current) {
      const allStillSaved = Array.from(dirtyFields).every((f) =>
        lastSavedFieldsRef.current?.has(f),
      );
      if (allStillSaved) {
        // These are the same fields we just saved — skip re-adding to pending.
        // Clear any phantom re-dirty flags left by input re-renders so the save
        // indicator can reach "Changes saved".
        const phantomFields = new Set(dirtyFields);
        lastSavedFieldsRef.current = null;
        if (phantomFields.size > 0) {
          clearSavedFields(phantomFields);
        }
        return;
      }
      // Some fields are genuinely new — clear the guard and proceed.
      lastSavedFieldsRef.current = null;
    }

    dirtyFields.forEach((field) => {
      if (!pausedAutoSaveFieldsRef.current.has(field)) {
        pendingFieldsRef.current.add(field);
      }
    });

    // If a save is in flight, record these as re-edited so the save's finally
    // block won't treat them as persisted (its request carried older values).
    // The watch effect only re-runs when the dirty set actually changes, so
    // this fires on genuine edits, not on incidental re-renders.
    if (isSavingRef.current) {
      dirtyFields.forEach((f) => reDirtiedDuringSaveRef.current.add(f));
    }

    const pendingCount = pendingFieldsRef.current.size;
    if (pendingCount === 0) return;

    setState((prev) => ({ ...prev, pendingChanges: pendingCount }));

    // Only reset timers if there were actually new dirty fields
    if (!hadNewFields) return;

    // Don't set a timer when dirtyFields has become empty (e.g., after
    // clearDirtyFields).  This prevents a second save from firing after
    // the first one completes.
    if (currentSnapshot === "") return;

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounce timer
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      void executeSave();
    }, debounceMs);

    // Set max wait timer if not already set
    if (!maxWaitTimerRef.current) {
      maxWaitTimerRef.current = setTimeout(() => {
        maxWaitTimerRef.current = null;
        void executeSave();
      }, maxWaitMs);
    }

    return () => {
      // Don't clear timers on cleanup — dirtyFields is a new Set reference
      // on every store update (even with same content). Clearing here would
      // kill the in-flight debounce timer when the effect re-runs due to
      // a reference change with no actual content change.
    };
  }, [
    dirtyFields,
    pausedAutoSaveFields,
    enabled,
    debounceMs,
    maxWaitMs,
    executeSave,
    clearSavedFields,
  ]);

  // When a field is unpaused, reschedule autosave for remaining dirty fields.
  React.useEffect(() => {
    const previousPaused = prevPausedFieldsRef.current;
    if (
      previousPaused.size > 0 &&
      hadPausedFieldRemoved(previousPaused, pausedAutoSaveFields)
    ) {
      lastDirtyFieldsSnapshotRef.current = "";
      dirtyFields.forEach((field) => {
        if (!pausedAutoSaveFields.has(field)) {
          pendingFieldsRef.current.add(field);
        }
      });
      const pendingCount = pendingFieldsRef.current.size;
      if (pendingCount > 0) {
        setState((prev) => ({ ...prev, pendingChanges: pendingCount }));
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
          debounceTimerRef.current = null;
          void executeSave();
        }, debounceMs);
      }
    }
    prevPausedFieldsRef.current = pausedAutoSaveFields;
  }, [pausedAutoSaveFields, dirtyFields, debounceMs, executeSave]);

  // Flush deferred fields once prerequisites (applicationId / coBorrowerId)
  // become available. Uses a separate ref to avoid coupling with the
  // success-only follow-up save in executeSave's finally block.
  React.useEffect(() => {
    if (!data.applicationId) return;
    if (deferredFieldsRef.current.size === 0) return;

    // Ensure lastDataRef has the latest data (with the now-available ids)
    lastDataRef.current = data;
    deferredFieldsRef.current.forEach((f) => pendingFieldsRef.current.add(f));
    deferredFieldsRef.current.clear();

    void executeSave();
  }, [data.applicationId, data.coBorrowerId, executeSave]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  return {
    ...state,
    forceSave,
    flushSave,
    cancelPendingSave,
  };
}

// ============================================================================
// Helper Hook for Simple Cases
// ============================================================================

/**
 * Simplified auto-save hook that uses a single save function for all fields.
 * The save function receives the full ApplicationFormData regardless of which fields changed.
 */
export function useSimpleAutoSave(
  data: ApplicationFormData,
  dirtyFields: Set<string>,
  clearSavedFields: (fields: Set<string>) => void,
  saveFunction: SaveHandler<ApplicationFormData>,
  options: AutoSaveOptions,
): UseDebouncedAutoSaveReturn {
  // Create a wrapper that always calls saveFunction with full data
  // We need to provide handlers for all possible handler keys since
  // getHandlerKeyForFieldPath maps fields to specific keys, not "default"
  const dataRef = React.useRef(data);
  dataRef.current = data;

  const handlers: SaveHandlerConfig = React.useMemo(() => {
    // Wrapper that ignores the slice and dirty fields, saves full data
    const saveFullData = (_data: unknown, _dirty: Set<string>) =>
      saveFunction(dataRef.current, _dirty);
    return {
      borrower: saveFullData,
      coBorrower: saveFullData,
      home: saveFullData,
      land: saveFullData,
      pricing: saveFullData,
      lenders: saveFullData,
      signature: saveFullData,
      default: saveFullData,
    };
  }, [saveFunction]);

  return useDebouncedAutoSave(
    data,
    dirtyFields,
    clearSavedFields,
    handlers,
    options,
  );
}
